import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { teamCalApi, TeamCalendar, CalPasswordState } from '../../utils/api/teamcal';

// Admin → Companies → Team calendars: the staff CalDAV mailboxes the Calendar shows (calendar_accounts).
// Add / edit / reorder / deactivate; a password is write-only (the server reports set / not set).

const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '7px 10px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none' };
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 20, margin: '16px 0' };
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '7px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' });
const iconBtn = (color = 'var(--text-secondary)'): React.CSSProperties => ({ border: 'none', background: 'none', color, cursor: 'pointer', padding: '2px 4px', fontSize: 15 });
const COLOR_RE = /^#[0-9A-Fa-f]{6}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Draft = { email: string; label: string; color: string; password: string };
const PW_STYLE: Record<CalPasswordState, { bg: string; fg: string; key: string }> = {
  none: { bg: '#FCEBEB', fg: '#E24B4A', key: 'teamcal_pw_none' },
  plain: { bg: '#FEF3C7', fg: '#B45309', key: 'teamcal_pw_plain' },
  sealed: { bg: '#E4F5EC', fg: '#1D9E75', key: 'teamcal_pw_sealed' },
  unreadable: { bg: '#FCEBEB', fg: '#E24B4A', key: 'teamcal_pw_unreadable' },
};

export default function TeamCalendars() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<TeamCalendar[]>([]);
  const [defaultColor, setDefaultColor] = useState('');
  const [editing, setEditing] = useState<string | null>(null);   // a row id, or 'new'
  const [draft, setDraft] = useState<Draft>({ email: '', label: '', color: '', password: '' });
  const [err, setErr] = useState(''); const [msg, setMsg] = useState(''); const [busy, setBusy] = useState(false);
  const [tests, setTests] = useState<Record<string, { ok: boolean; text: string } | 'running'>>({});
  const [showInactive, setShowInactive] = useState(false);

  const load = () => teamCalApi.list().then(d => { setRows(d.accounts); setDefaultColor(d.defaultColor); }).catch((e: any) => setErr(e.message));
  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const active = rows.filter(r => r.active);
  const inactive = rows.filter(r => !r.active);
  const plainCount = rows.filter(r => r.passwordState === 'plain').length;
  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };
  const failMsg = (e: any) => e?.status === 409 ? t('teamcal_err_dup') : (e?.message || 'failed');

  const startNew = () => { setErr(''); setEditing('new'); setDraft({ email: '', label: '', color: defaultColor, password: '' }); };
  const startEdit = (r: TeamCalendar) => { setErr(''); setEditing(r.id); setDraft({ email: r.email, label: r.label, color: r.color, password: '' }); };

  const save = async () => {
    const isNew = editing === 'new';
    const problem = !EMAIL_RE.test(draft.email.trim()) ? 'teamcal_err_email'
      : !draft.label.trim() ? 'teamcal_err_label'
      : !COLOR_RE.test(draft.color.trim()) ? 'teamcal_err_color'
      : isNew && !draft.password ? 'teamcal_err_password' : '';
    if (problem) { setErr(t(problem)); return; }
    setBusy(true); setErr('');
    try {
      const body = { email: draft.email.trim(), label: draft.label.trim(), color: draft.color.trim(), ...(draft.password ? { password: draft.password } : {}) };
      if (isNew) await teamCalApi.create(body); else await teamCalApi.update(editing!, body);
      setEditing(null); setDraft({ email: '', label: '', color: '', password: '' });
      await load(); flash(t('cs_saved'));
    } catch (e: any) { setErr(failMsg(e)); } finally { setBusy(false); }
  };

  const move = async (id: string, dir: -1 | 1) => {
    const ids = active.map(r => r.id);
    const i = ids.indexOf(id), j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try { setRows((await teamCalApi.reorder(ids)).accounts); } catch (e: any) { setErr(failMsg(e)); }
  };

  const setActive = async (r: TeamCalendar, on: boolean) => {
    if (!on && !window.confirm(t('teamcal_confirm_deactivate', { label: r.label }))) return;
    try { await teamCalApi.update(r.id, { active: on }); await load(); } catch (e: any) { setErr(failMsg(e)); }
  };

  const test = async (r: TeamCalendar) => {
    setTests(s => ({ ...s, [r.id]: 'running' }));
    try {
      const x = await teamCalApi.test(r.id);
      setTests(s => ({ ...s, [r.id]: { ok: x.ok, text: x.ok ? t('teamcal_test_ok', { n: x.calendars }) : t('teamcal_test_fail', { error: x.error || x.status }) } }));
    } catch (e: any) { setTests(s => ({ ...s, [r.id]: { ok: false, text: t('teamcal_test_fail', { error: e?.message || '' }) } })); }
  };

  const seal = async () => {
    setBusy(true); setErr('');
    try { const d = await teamCalApi.seal(); setRows(d.accounts); flash(t('teamcal_sealed_msg', { n: d.sealed })); }
    catch (e: any) { setErr(failMsg(e)); } finally { setBusy(false); }
  };

  const editor = (isNew: boolean) => (
    <div style={{ background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 12, margin: '6px 0 10px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={lbl}>{t('teamcal_email')}</label>
          <input value={draft.email} onChange={e => setDraft({ ...draft, email: e.target.value })} style={input} type="email" autoComplete="off" placeholder="name@…" />
        </div>
        <div>
          <label style={lbl}>{t('teamcal_label')}</label>
          <input value={draft.label} onChange={e => setDraft({ ...draft, label: e.target.value })} style={input} maxLength={60} />
        </div>
        <div>
          <label style={lbl}>{t('teamcal_color')}</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="color" value={COLOR_RE.test(draft.color) ? draft.color : '#000000'} onChange={e => setDraft({ ...draft, color: e.target.value.toUpperCase() })}
              style={{ width: 36, height: 32, padding: 0, border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)', background: 'none', cursor: 'pointer' }} />
            <input value={draft.color} onChange={e => setDraft({ ...draft, color: e.target.value })} style={{ ...input, fontFamily: 'monospace' }} maxLength={7} placeholder="#RRGGBB" />
          </div>
        </div>
        <div>
          <label style={lbl}>{t('teamcal_password')}</label>
          <input value={draft.password} onChange={e => setDraft({ ...draft, password: e.target.value })} style={input} type="password" autoComplete="new-password"
            placeholder={isNew ? '' : t('teamcal_password_keep')} />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
        <button onClick={() => { setEditing(null); setErr(''); }} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('cancel')}</button>
        <button onClick={save} disabled={busy} style={btn('var(--accent)')}>{busy ? t('login_wait') : t('save')}</button>
      </div>
    </div>
  );

  const row = (r: TeamCalendar, i: number, list: TeamCalendar[]) => {
    const pw = PW_STYLE[r.passwordState] || PW_STYLE.none;
    const tr = tests[r.id];
    return (
      <div key={r.id}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)', opacity: r.active ? 1 : 0.7 }}>
          <span style={{ width: 14, height: 14, borderRadius: 4, background: r.color, flexShrink: 0, border: '1px solid var(--border)' }} title={r.color} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              {r.label}
              {r.shownAs && r.shownAs !== r.label && <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }} title={t('teamcal_shown_as_hint')}> · {t('teamcal_shown_as', { name: r.shownAs })}</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</div>
            {tr && <div style={{ fontSize: 11.5, marginTop: 2, color: tr === 'running' ? 'var(--text-tertiary)' : tr.ok ? '#1D9E75' : '#E24B4A' }}>{tr === 'running' ? t('teamcal_testing') : tr.text}</div>}
          </div>
          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: pw.bg, color: pw.fg, whiteSpace: 'nowrap' }}>{t(pw.key)}</span>
          <span style={{ whiteSpace: 'nowrap' }}>
            {r.active && <>
              <button onClick={() => move(r.id, -1)} disabled={i === 0} title={t('teamcal_move_up')} style={{ ...iconBtn(), opacity: i === 0 ? 0.3 : 1 }}><i className="ti ti-arrow-up" /></button>
              <button onClick={() => move(r.id, 1)} disabled={i === list.length - 1} title={t('teamcal_move_down')} style={{ ...iconBtn(), opacity: i === list.length - 1 ? 0.3 : 1 }}><i className="ti ti-arrow-down" /></button>
              <button onClick={() => test(r)} disabled={tr === 'running'} title={t('teamcal_test')} style={iconBtn()}><i className="ti ti-plug-connected" /></button>
            </>}
            <button onClick={() => startEdit(r)} title={t('edit')} style={iconBtn()}><i className="ti ti-pencil" /></button>
            {r.active
              ? <button onClick={() => setActive(r, false)} title={t('teamcal_deactivate')} style={iconBtn('#E24B4A')}><i className="ti ti-eye-off" /></button>
              : <button onClick={() => setActive(r, true)} title={t('teamcal_reactivate')} style={iconBtn('var(--accent)')}><i className="ti ti-eye" /></button>}
          </span>
        </div>
        {editing === r.id && editor(false)}
      </div>
    );
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>{t('teamcal_title')}</div>
        {msg && <span style={{ fontSize: 12, color: '#1D9E75' }}><i className="ti ti-check" style={{ marginRight: 4 }} />{msg}</span>}
        {plainCount > 0 && <button onClick={seal} disabled={busy} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}><i className="ti ti-lock" style={{ marginRight: 4 }} />{t('teamcal_seal', { n: plainCount })}</button>}
        {editing !== 'new' && <button onClick={startNew} style={btn('var(--accent)')}><i className="ti ti-plus" style={{ marginRight: 4 }} />{t('teamcal_add')}</button>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '4px 0 10px' }}>{t('teamcal_hint')}</div>
      {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 10 }}>{err}</div>}
      {editing === 'new' && editor(true)}
      {active.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '6px 0' }}>{t('teamcal_none')}</div>}
      {active.map((r, i) => row(r, i, active))}
      {inactive.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button onClick={() => setShowInactive(v => !v)} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
            <i className={`ti ti-chevron-${showInactive ? 'down' : 'right'}`} style={{ marginRight: 4 }} />{t('teamcal_inactive', { n: inactive.length })}
          </button>
          {showInactive && inactive.map((r, i) => row(r, i, inactive))}
        </div>
      )}
    </div>
  );
}
